import { AlertTriangle, CheckCircle2, Loader2, RefreshCw } from 'lucide-react'
import { cn, formatKickoff } from '@/lib/utils'
import type { AnalysisJob, QuinielaCurrent } from '@/types/quiniela'

export function isActive(job: AnalysisJob | null | undefined): boolean {
  return !!job && (job.status === 'pending' || job.status === 'running')
}

/** Estado de frescura de La Quiniela: aviso neutro (el análisis ya es válido), hora del análisis y del último
 * dato, y -cuando corresponde- Actualizar análisis. Durante un reanálisis muestra progreso REAL procesados/total
 * sin ocultar el análisis anterior. */
export function FreshnessBanner({
  current, onUpdate, updating, starting,
}: {
  current: QuinielaCurrent
  onUpdate: () => void
  updating: boolean
  starting: boolean
}) {
  const snap = current.snapshot
  const job = current.analysis
  const active = isActive(job)
  const fresh = current.freshness
  const first = !snap
  const processed = job?.progress.processed ?? 0
  const total = job?.progress.total ?? 15
  const pct = Math.min(100, Math.round((processed / Math.max(total, 1)) * 100))

  if (active || (first && starting)) {
    return (
      <section className="card !p-3.5 border-brand/25 space-y-2" aria-live="polite">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-[13px] font-bold text-text">
            <Loader2 className="h-4 w-4 animate-spin text-brand motion-reduce:animate-none" />
            <span>{first ? 'Analizando la jornada' : 'Actualizando análisis'} · <span className="figure">{processed}/{total}</span></span>
          </div>
          <span className="figure text-[11px] text-text-dim">{pct}%</span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-border overflow-hidden" role="progressbar" aria-valuenow={processed} aria-valuemin={0} aria-valuemax={total}>
          <div className="h-full bg-brand transition-[width] duration-500" style={{ width: `${pct}%` }} />
        </div>
        <p className="text-[11px] text-text-muted leading-snug">
          {first
            ? 'JA calcula las probabilidades de los 15 partidos con la información disponible ahora.'
            : 'Puedes seguir usando la jornada: el análisis anterior se mantiene hasta que termine el nuevo.'}
        </p>
      </section>
    )
  }

  if (first) {
    return (
      <section className="card space-y-2 border-warn/30">
        <div className="flex items-center gap-2 text-[13px] font-bold text-text">
          <AlertTriangle className="h-4 w-4 text-warn" /> Todavía no hay análisis de esta jornada
        </div>
        {job?.status === 'failed' && <p className="text-[11px] text-warn">{job.error}</p>}
        <button type="button" onClick={onUpdate} className="btn-brand-app w-full">Analizar jornada</button>
      </section>
    )
  }

  const updateAvailable = !!fresh?.update_available
  const canUpdate = !!fresh?.can_update
  const failed = job?.status === 'failed' ? job : null
  const message = snap!.notice.message

  return (
    <section className={cn('card !p-3.5 space-y-2.5', updateAvailable ? 'border-cyan/35' : 'border-border')}>
      <div className="flex items-start gap-2.5">
        <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-brand" />
        <p className="text-[12px] leading-snug text-text">{message}</p>
      </div>

      {failed && (
        <p className="text-[11px] text-warn flex items-start gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
          <span>{failed.error || 'No se pudo completar la última actualización. Tu análisis sigue disponible.'}</span>
        </p>
      )}
      {snap!.incomplete.length > 0 && (
        <p className="text-[11px] text-warn flex items-start gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
          <span>
            {snap!.incomplete.length === 1 ? 'Un partido no tiene' : `${snap!.incomplete.length} partidos no tienen`} probabilidades todavía
            (casilla {snap!.incomplete.join(', ')}). JA los cubre como triple en Créala con JA.
          </span>
        </p>
      )}

      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0 space-y-0.5 text-[11px] text-text-dim whitespace-nowrap">
          <p>Analizada: <span className="figure text-text-muted">{formatKickoff(snap!.generated_at)}</span></p>
          {snap!.notice.last_data_update && (
            <p>Último dato: <span className="figure text-text-muted">{formatKickoff(snap!.notice.last_data_update + (snap!.notice.last_data_update.endsWith('Z') || snap!.notice.last_data_update.includes('+') ? '' : 'Z'))}</span></p>
          )}
          {updateAvailable && <p className="text-cyan font-semibold">Hay información más reciente disponible.</p>}
        </div>
        {canUpdate && (
          <button
            type="button" onClick={onUpdate} disabled={updating}
            className={cn(updateAvailable ? 'btn-brand-app' : 'btn-secondary-app', '!py-2 !px-3 !text-[11px] shrink-0 disabled:opacity-50')}
          >
            <RefreshCw className={cn('h-3.5 w-3.5', updating && 'animate-spin')} /> Actualizar análisis
          </button>
        )}
      </div>
    </section>
  )
}
